import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '.';
import {
  getPlaylist,
  editPlaylistData,
  playlistDeleteItem,
  playlistKudo,
  playlistAddItem,
} from 'services/post';
import { formatDuration } from '../../lib/helpers';
import { notifySuccess, notifyError } from '../notifications';

const initState = {
  PlaylistItemMap: {},
  title: '',
  kudos: 0,
  description: '',
  follows: 0,
  uploader: 0,
  private: undefined,
  created_at: '',
};

export const PlaylistContext = React.createContext({});
PlaylistContext.displayName = 'Playlists';

const convertObj = (list) => Object.keys(list).map((key) => ({ position: key, ...list[key] }));

export const PlaylistContextProvider = ({ children }) => {
  const currentItemId = useParams().itemId;
  const currentUserId = useParams().userId;
  const currentPlaylistId = useParams().playlistId;

  const navigate = useNavigate();

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [playingItemId, setPlayingItemId] = useState('');
  const [playListData, setPlayListData] = useState(initState);
  const [isPrivateList, setIsPrivateList] = useState(false);

  const [name, setName] = useState();
  const [desc, setDesc] = useState('');
  const [editTitleActive, setEditTitleActive] = useState(false);
  const [editDescActive, setEditDescActive] = useState(false);

  const { PlaylistItemMap, uploader } = playListData || {};
  const playlistItems = playListData.PlaylistItemMap ? convertObj(PlaylistItemMap) : [];

  const totalPlaylistDuration = PlaylistItemMap
    ? formatDuration(playlistItems.reduce((acc, item) => acc + item.media[0].duration, 0))
    : 0;

  const userCanEdit = user && user.dumpertId ? user.dumpertId === uploader : false;

  const getCurrentPlaylist = async () =>
    getPlaylist({ userId: currentUserId, playlistId: currentPlaylistId })
      .then((playlist) => {
        if (playlist.data) {
          setPlayListData(playlist.data);
          const setPlayingId =
            currentItemId ||
            (convertObj(playlist.data.PlaylistItemMap).length > 0
              ? convertObj(playlist.data.PlaylistItemMap)[0].composite_id
              : 0);
          setPlayingItemId(setPlayingId);
          setName(playlist.data.title);
          setDesc(playlist.data.description);
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error.response && error.response.status === 401) {
          setIsPrivateList(true);
        } else if (error.response && error.response.status === 404) {
          navigate('/404');
        } else {
          notifyError(error);
        }
      });

  useEffect(() => {
    setPlayListData(initState);
  }, []);

  // useLayoutEffect does nothing on the server,
  // because its effect cannot be encoded into the server renderer's output format.
  // This will lead to a mismatch between the initial, non-hydrated UI and the intended UI.
  // To avoid this, useLayoutEffect should only be used in components that render exclusively on the client.
  // See https://reactjs.org/link/uselayouteffect-ssr for common fixes.
  // useLayoutEffect(() => {

  useEffect(() => {
    setLoading(true);
    setIsPrivateList(false);
    getCurrentPlaylist();
  }, [currentItemId, currentPlaylistId]);

  const addItemToPlaylist = async (obj) =>
    playlistAddItem({ ...obj })
      .then(() => {
        setTimeout(() => {
          notifySuccess('Item aan afspeellijst toegevoegd');
          getCurrentPlaylist();
        }, 50);
      })
      .catch((error) => notifyError(error));

  const deleteItemFromPlaylist = async (obj) =>
    playlistDeleteItem({ ...obj })
      .then(() => {
        setTimeout(() => {
          notifySuccess('Item verwijderd');
          getCurrentPlaylist();
        }, 50);
      })
      .catch((error) => notifyError(error));

  const handleEditPlaylist = async (setPrivateList) => {
    editPlaylistData({
      playlistId: currentPlaylistId,
      title: name,
      description: desc,
      isPrivate: setPrivateList,
    })
      .then(() => {
        setPlayListData({
          ...playListData,
          title: name,
          description: desc,
          private: setPrivateList,
        });
        notifySuccess('wijzigingen zijn opgeslagen');
      })
      .catch((error) => notifyError(error));
  };

  const playlistKudoAction = async (obj) =>
    playlistKudo({ ...obj })
      .then(() => {
        notifySuccess('Kudo toegevoegd');
      })
      .catch((error) => notifyError(error));

  return (
    <PlaylistContext.Provider
      value={{
        playingItemId,
        currentUserId,
        currentPlaylistId,
        playlistItems,
        userCanEdit,
        loading,
        isPrivateList,
        addItemToPlaylist,
        playListData,
        getCurrentPlaylist,
        totalPlaylistDuration,
        deleteItemFromPlaylist,
        handleEditPlaylist,
        editTitleActive,
        setEditTitleActive,
        editDescActive,
        setEditDescActive,
        name,
        setName,
        desc,
        setDesc,
        playlistKudoAction,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
};
